/**
 * 我的工具箱页面
 * 管理常用的药品、工具、营养品等
 * 使用 shadcn ui + Vercel 黑白主题
 */

import React, { useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  MoreHorizontal,
  Trash2,
  Pencil,
  Check,
  X,
  Pill,
  Wrench,
  Droplet,
  Apple,
  TestTube,
  Package,
  Plus,
  Shield,
  TrendingUp,
  Sparkles,
  Thermometer,
  Wind,
  ShieldAlert,
  Lightbulb,
  ShoppingBag,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import SubsectionHeader from "@/components/shared/SubsectionHeader";
import { toolboxService } from "@/services/toolboxService";
import type {
  ToolboxItem as ServiceToolboxItem,
  RegulatoryTier,
} from "@/services/toolboxService";
import {
  ComplianceBadge,
  ComplianceDot,
} from "@/components/shared/ComplianceBadge";

import GlobalProductPicker from "@/components/GlobalProductPicker";

// ==================== 类型定义 ====================
interface ToolboxItem {
  id: number;
  item_type: string;
  name: string;
  display_name?: string; // 多语言显示名称
  tag_slug?: string | null;
  created_at: string;
  // 模板相关字段
  toolbox_template_id?: number | null;
  use_template_name?: boolean;
  template_name?: string | null;
  display_icon?: string | null;
  thumbnail_url?: string | null;
  // 合规字段
  regulatory_tier?: RegulatoryTier;
  compliance_warning?: string | null;
  is_consumable?: boolean;
  // 有效期字段
  expire_at?: string | null;
  opened_at?: string | null;
  status?: "good" | "expiring_soon" | "expired";
  // 详情字段
  brand?: string | null;
  model?: string | null;
  concentration_ratio?: number;
  effective_concentration_ratio?: number;
  global_product_id?: number | null;
  quantity?: number | null;
  unit?: string | null;
}

interface ToolboxCategory {
  id: number;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
}

// 图标映射（根据 slug 匹配 Lucide 图标）
const ICON_MAP: Record<string, React.ElementType> = {
  // 新的 slug
  medications: Pill,
  "water-conditioners": Droplet,
  "testing-kits": TestTube,
  filtration: Wrench,
  "heating-cooling": Thermometer,
  aeration: Wind,
  "food-nutrition": Apple,
  quarantine: ShieldAlert,
  maintenance: Wrench,
  lighting: Lightbulb,
  // 旧的 slug（兼容）
  med: Pill,
  tool: Wrench,
  supplement: Droplet,
  feed: Apple,
  test: TestTube,
  other: Package,
};

// 获取分类图标（优先使用数据库中的 emoji，否则使用 Lucide 图标）
const getCategoryIcon = (
  category: ToolboxCategory,
): React.ElementType | string => {
  // 如果有 emoji 图标，直接返回
  if (category.icon) return category.icon;
  // 否则使用 ICON_MAP
  if (ICON_MAP[category.slug]) return ICON_MAP[category.slug];
  return Package;
};

// ==================== API 函数 ====================
// 使用 toolboxService 进行 API 调用

// 计算覆盖率
const calculateCoverage = (data: Record<string, ToolboxItem[]> | undefined) => {
  if (!data) return 0;
  const medCount =
    (data.medications?.length || 0) + (data.medicine?.length || 0);
  const toolCount = data.equipment?.length || 0;
  const testCount =
    (data["testing-kits"]?.length || 0) + (data["test-kits"]?.length || 0);
  const conditionerCount =
    (data["water-conditioners"]?.length || 0) + (data.conditioner?.length || 0);
  const score =
    Math.min(medCount * 15, 50) +
    Math.min(toolCount * 10, 30) +
    Math.min(testCount * 10, 15) +
    Math.min(conditionerCount * 5, 5);
  return Math.min(score, 100);
};

// ==================== 主组件 ====================
export default function Toolbox() {
  const { t, i18n } = useTranslation("common");
  const queryClient = useQueryClient();

  // 获取分类列表（根据当前语言）
  const { data: categories = [] } = useQuery({
    queryKey: ["toolbox-categories", i18n.language],
    queryFn: () => toolboxService.getCategories(),
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["toolbox", i18n.language],
    queryFn: () => toolboxService.getToolboxItems(),
  });

  // 状态定义 - 必须在使用它们的函数之前
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState({
    item_type: "med",
    name: "",
    brand: "",
    model: "",
    expire_at: "",
    opened_at: "",
    quantity: 100,
    unit: "%",
    concentration_ratio: 1.0,
    global_product_id: null as number | null,
  });
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // 使用 ref 同步追踪删除状态，避免异步状态更新导致的重复请求
  const deletingRef = useRef<number | null>(null);
  // 使用 ref 存储待删除的 itemId，避免闭包问题
  const deleteItemIdRef = useRef<number | null>(null);

  // 删除确认弹窗状态
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    itemId: number | null;
    itemName: string;
  }>({
    open: false,
    itemId: null,
    itemName: "",
  });

  const deleteMutation = useMutation({
    mutationFn: toolboxService.deleteItem.bind(toolboxService),
    onSuccess: () => {
      toast.success(t("toolbox.toast.deleteSuccess"));
      queryClient.invalidateQueries({ queryKey: ["toolbox"] });

      deletingRef.current = null;
      setDeletingId(null);
    },
    onError: (error: any) => {
      // 如果是 404 错误，说明物品已经被删除，视为成功
      if (error?.status === 404 || error?.isNotFound?.()) {
        toast.success(t("toolbox.toast.deleteSuccess"));
      } else {
        toast.error(t("toolbox.toast.deleteFailed"));
      }
      // 无论如何都刷新列表
      queryClient.invalidateQueries({ queryKey: ["toolbox"] });

      deletingRef.current = null;
      setDeletingId(null);
    },
  });

  // 处理删除操作 - 使用 ref 同步检查防止重复调用
  const handleDelete = useCallback(() => {
    // 从 ref 获取 itemId，避免闭包问题
    const itemId = deleteItemIdRef.current;

    if (!itemId) {
      return;
    }

    // 同步检查：使用 ref 立即阻止重复调用
    if (deletingRef.current !== null) {
      return;
    }

    // 立即设置 ref，阻止后续调用
    deletingRef.current = itemId;
    // 清除待删除 ref
    deleteItemIdRef.current = null;

    setDeletingId(itemId);

    // 先关闭弹窗
    setDeleteConfirm({ open: false, itemId: null, itemName: "" });

    // 然后执行删除
    deleteMutation.mutate(itemId);
  }, [deleteMutation]);

  // 打开删除确认弹窗
  const openDeleteConfirm = (item: ToolboxItem) => {
    // 如果弹窗已打开或正在删除，不重复打开
    if (deleteConfirm.open || deletingRef.current !== null) {
      return;
    }
    // 同时设置 ref 和 state
    deleteItemIdRef.current = item.id;
    setDeleteConfirm({
      open: true,
      itemId: item.id,
      itemName: item.display_name || item.name,
    });
  };

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...payload
    }: { id: number } & Partial<Omit<ToolboxItem, "id" | "created_at">>) =>
      toolboxService.updateItem(id, payload),
    onSuccess: () => {
      toast.success(t("toolbox.toast.updateSuccess"));
      queryClient.invalidateQueries({ queryKey: ["toolbox"] });
      setEditingId(null);
    },
    onError: () => toast.error(t("toolbox.toast.updateFailed")),
  });

  const calculateSmartExpiration = (openedAt: string, itemType: string) => {
    if (!openedAt) return "";
    const date = new Date(openedAt);
    if (isNaN(date.getTime())) return "";

    let monthsToAdd = 12;
    const type = itemType.toLowerCase();
    if (["med", "medications"].includes(type)) monthsToAdd = 6;
    else if (["test", "testing-kits"].includes(type)) monthsToAdd = 12;
    else if (["feed", "food-nutrition"].includes(type)) monthsToAdd = 3;
    else if (["supplement", "water-conditioners"].includes(type))
      monthsToAdd = 6;

    date.setMonth(date.getMonth() + monthsToAdd);
    return date.toISOString().split("T")[0];
  };

  const handleOpenedAtChange = (val: string) => {
    setEditDraft((prev) => {
      const smartExp = !prev.expire_at
        ? calculateSmartExpiration(val, prev.item_type)
        : prev.expire_at;
      return { ...prev, opened_at: val, expire_at: smartExp || prev.expire_at };
    });
  };

  const [showProductPicker, setShowProductPicker] = useState(false);

  const stats = useMemo(() => {
    if (!data) return { total: 0, categories: 0, coverage: 0 };
    const total = Object.values(data).reduce(
      (sum, items) => sum + items.length,
      0,
    );
    const categories = Object.values(data).filter(
      (items) => items.length > 0,
    ).length;
    const coverage = calculateCoverage(data);
    return { total, categories, coverage };
  }, [data]);

  return (
    <div className="container mx-auto max-w-4xl px-4 py-4">
      <GlobalProductPicker
        open={showProductPicker}
        onOpenChange={setShowProductPicker}
        onProductAdded={() => {
          // 刷新列表
          queryClient.invalidateQueries({ queryKey: ["toolbox"] });
        }}
      />

      <SubsectionHeader
        title={t("toolbox.title")}
        icon="Wrench"
        description={t("toolbox.description")}
        className="mb-6"
      />

      {/* 资产仪表盘 */}
      {!isLoading && stats.total > 0 && (
        <Card className="border-border mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-6">
              <div className="flex flex-col items-center justify-center py-4">
                <Package className="h-5 w-5 text-muted-foreground mb-2" />
                <span className="text-3xl font-semibold">{stats.total}</span>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("toolbox.stats.totalItems")}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center py-4 border-x border-border">
                <TrendingUp className="h-5 w-5 text-muted-foreground mb-2" />
                <span className="text-3xl font-semibold">
                  {stats.categories}
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("toolbox.stats.categories")}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center py-4">
                <Shield className="h-5 w-5 text-muted-foreground mb-2" />
                <span className="text-3xl font-semibold">
                  {stats.coverage}%
                </span>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("toolbox.stats.coverage")}
                </p>
              </div>
            </div>
            {stats.coverage >= 80 && (
              <div className="mt-4 pt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                <span>{t("toolbox.tips.excellent")}</span>
              </div>
            )}
            {stats.coverage < 80 && stats.coverage >= 50 && (
              <div className="mt-4 pt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                <span>{t("toolbox.tips.good")}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 快速添加按钮 */}
      <Button
        onClick={() => setShowProductPicker(true)}
        variant="default"
        className="w-full mb-6 h-10 justify-center gap-2"
      >
        <Plus className="h-4 w-4" />
        {t("toolbox.product.quickAdd", "添加商品")}
      </Button>

      {/* 列表 */}
      {isLoading ? (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-border">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="ml-auto h-5 w-8 rounded-full" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {[1, 2, 3, 4].map((j) => (
                    <Skeleton key={j} className="h-10 rounded-lg" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data && Object.keys(data).length > 0 ? (
        <div className="space-y-6">
          {/* 已知分类的物品 */}
          {categories.map((cat) => {
            const items = data[cat.slug] || [];
            if (items.length === 0) return null;

            const icon = getCategoryIcon(cat);
            return (
              <Card key={cat.slug} className="border-border">
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {typeof icon === "string" ? (
                      <span className="text-base">{icon}</span>
                    ) : (
                      React.createElement(icon, {
                        className: "h-4 w-4",
                        fill: "currentColor",
                      })
                    )}
                    {cat.name}
                    <Badge
                      variant="secondary"
                      className="ml-auto text-xs font-normal"
                    >
                      {items.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center justify-between gap-2 px-3 py-2.5",
                          "bg-secondary/50 border border-border rounded-lg",
                          "hover:bg-secondary transition-colors group",
                        )}
                      >
                        <span
                          className="flex-1 text-sm truncate flex items-center gap-2"
                          title={item.display_name || item.name}
                        >
                          {/* 商品缩略图 */}
                          <div className="h-10 w-10 shrink-0 rounded-md bg-white border border-border p-0.5 overflow-hidden flex items-center justify-center">
                            {item.thumbnail_url ? (
                              <img
                                src={item.thumbnail_url}
                                alt={item.name}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-muted-foreground/40" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            {/* 第一行：自定义名称 + 合规点 */}
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-semibold text-sm truncate">
                                {item.display_name || item.name}
                              </span>
                            </div>

                            {/* 第二行：品牌与型号（仅在不与主标题重复时显示） */}
                            {(() => {
                              const displayName =
                                item.display_name || item.name;
                              const brandModel = [item.brand, item.model]
                                .filter(Boolean)
                                .join(" ");
                              if (!brandModel || brandModel === displayName)
                                return null;
                              return (
                                <div className="text-[10px] text-muted-foreground truncate leading-tight">
                                  {brandModel}
                                </div>
                              );
                            })()}

                            {/* 第三行：有效期状态 + 浓度系数 */}
                            <div className="flex items-center gap-2 mt-0.5">
                              {/* 有效期状态点 */}
                              <div className="flex items-center gap-1">
                                <div
                                  className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    item.status === "expired"
                                      ? "bg-red-500 animate-pulse"
                                      : item.status === "expiring_soon"
                                        ? "bg-yellow-500"
                                        : "bg-green-500",
                                  )}
                                />
                                <span className="text-[10px] whitespace-nowrap text-muted-foreground">
                                  {item.status === "expired"
                                    ? t("toolbox.status.expired", "已过期")
                                    : item.status === "expiring_soon"
                                      ? t(
                                          "toolbox.status.expiring_soon",
                                          "即将过期",
                                        )
                                      : t("toolbox.status.good", "正常")}
                                  {item.expire_at && (
                                    <span className="ml-1 opacity-70">
                                      ({item.expire_at.split("T")[0]})
                                    </span>
                                  )}
                                </span>
                              </div>

                              {/* 浓度系数 */}
                              {item.concentration_ratio &&
                                item.concentration_ratio !== 1 && (
                                  <Badge
                                    variant="outline"
                                    className="h-3.5 px-1 text-[9px] border-primary/20 text-primary/70 bg-primary/5"
                                  >
                                    x{item.concentration_ratio}
                                  </Badge>
                                )}
                            </div>
                          </div>
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditingId(item.id);
                                setEditDraft({
                                  item_type: item.item_type,
                                  name: item.name,
                                  brand: item.brand || "",
                                  model: item.model || "",
                                  expire_at: item.expire_at
                                    ? item.expire_at.split("T")[0]
                                    : "",
                                  opened_at: item.opened_at
                                    ? item.opened_at.split("T")[0]
                                    : "",
                                  quantity: item.quantity || 100,
                                  unit: item.unit || "%",
                                  concentration_ratio:
                                    item.concentration_ratio || 1.0,
                                  global_product_id:
                                    item.global_product_id || null,
                                });
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t("toolbox.actions.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => openDeleteConfirm(item)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("toolbox.actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* 未分类的物品（不在已知分类中的） */}
          {(() => {
            const knownSlugs = new Set(categories.map((c) => c.slug));
            const uncategorizedItems = Object.entries(data)
              .filter(([slug]) => !knownSlugs.has(slug))
              .flatMap(([, items]) => items);

            if (uncategorizedItems.length === 0) return null;

            return (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    {t("toolbox.categories.other", "其他")}
                    <Badge
                      variant="secondary"
                      className="ml-auto text-xs font-normal"
                    >
                      {uncategorizedItems.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2">
                    {uncategorizedItems.map((item) => (
                      <div
                        key={item.id}
                        className={cn(
                          "flex items-center justify-between gap-2 px-3 py-2.5",
                          "bg-secondary/50 border border-border rounded-lg",
                          "hover:bg-secondary transition-colors group",
                        )}
                      >
                        <span
                          className="flex-1 text-sm truncate flex items-center gap-2"
                          title={item.display_name || item.name}
                        >
                          <div className="h-10 w-10 shrink-0 rounded-md bg-white border border-border p-0.5 overflow-hidden flex items-center justify-center">
                            {item.thumbnail_url ? (
                              <img
                                src={item.thumbnail_url}
                                alt={item.name}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-muted-foreground/40" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-semibold text-sm truncate">
                                {item.display_name || item.name}
                              </span>
                            </div>

                            {(() => {
                              const displayName =
                                item.display_name || item.name;
                              const brandModel = [item.brand, item.model]
                                .filter(Boolean)
                                .join(" ");
                              if (!brandModel || brandModel === displayName)
                                return null;
                              return (
                                <div className="text-[10px] text-muted-foreground truncate leading-tight">
                                  {brandModel}
                                </div>
                              );
                            })()}

                            <div className="flex items-center gap-2 mt-0.5">
                              <div className="flex items-center gap-1">
                                <div
                                  className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    item.status === "expired"
                                      ? "bg-red-500 animate-pulse"
                                      : item.status === "expiring_soon"
                                        ? "bg-yellow-500"
                                        : "bg-green-500",
                                  )}
                                />
                                <span className="text-[10px] whitespace-nowrap text-muted-foreground">
                                  {item.status === "expired"
                                    ? t("toolbox.status.expired", "已过期")
                                    : item.status === "expiring_soon"
                                      ? t(
                                          "toolbox.status.expiring_soon",
                                          "即将过期",
                                        )
                                      : t("toolbox.status.good", "正常")}
                                  {item.expire_at && (
                                    <span className="ml-1 opacity-70">
                                      ({item.expire_at.split("T")[0]})
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditingId(item.id);
                                setEditDraft({
                                  item_type: item.item_type,
                                  name: item.name,
                                  brand: item.brand || "",
                                  model: item.model || "",
                                  expire_at: item.expire_at
                                    ? item.expire_at.split("T")[0]
                                    : "",
                                  opened_at: item.opened_at
                                    ? item.opened_at.split("T")[0]
                                    : "",
                                  quantity: item.quantity || 100,
                                  unit: item.unit || "%",
                                  concentration_ratio:
                                    item.concentration_ratio || 1.0,
                                  global_product_id:
                                    item.global_product_id || null,
                                });
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t("toolbox.actions.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => openDeleteConfirm(item)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("toolbox.actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      ) : (
        <Card className="border-border">
          <CardContent className="p-6 py-16">
            <div className="text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground mb-1">
                {t("toolbox.empty.title")}
              </p>
              <p className="text-sm text-muted-foreground/70">
                {t("toolbox.empty.description")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 编辑弹窗 */}
      <Dialog
        open={editingId !== null}
        onOpenChange={(open) => !open && setEditingId(null)}
      >
        <DialogContent className="sm:max-w-[440px] border-border bg-background p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-xl font-bold">
              {t("toolbox.dialog.editTitle", "编辑详情")}
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-6">
            {/* 基本信息 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor="name" className="w-24 text-right shrink-0">
                    {t("toolbox.nameLabel")}
                  </Label>
                  <Input
                    id="name"
                    value={editDraft.name}
                    onChange={(e) =>
                      setEditDraft((p) => ({ ...p, name: e.target.value }))
                    }
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="w-24 text-right shrink-0">
                    {t("toolbox.dialog.brand", "品牌")}
                  </Label>
                  <Input
                    value={editDraft.brand}
                    disabled={!!editDraft.global_product_id}
                    onChange={(e) =>
                      setEditDraft((p) => ({ ...p, brand: e.target.value }))
                    }
                    className="flex-1"
                    placeholder={
                      editDraft.global_product_id ? "" : "e.g. Seachem"
                    }
                  />
                </div>
              </div>
            </div>

            {/* 效期信息 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="w-24 text-right shrink-0">
                    {t("toolbox.openedAt")}
                  </Label>
                  <DatePicker
                    value={
                      editDraft.opened_at
                        ? new Date(editDraft.opened_at)
                        : undefined
                    }
                    onChange={(date) =>
                      handleOpenedAtChange(
                        date ? date.toISOString().split("T")[0] : "",
                      )
                    }
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="w-24 text-right shrink-0">
                    {t("toolbox.expireAt")}
                  </Label>
                  <DatePicker
                    value={
                      editDraft.expire_at
                        ? new Date(editDraft.expire_at)
                        : undefined
                    }
                    onChange={(date) =>
                      setEditDraft((p) => ({
                        ...p,
                        expire_at: date ? date.toISOString().split("T")[0] : "",
                      }))
                    }
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* 库存管理 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="w-24 text-right shrink-0">
                    {t("toolbox.dialog.quantity", "剩余容量")}
                  </Label>
                  <div className="flex-1 flex items-center gap-3">
                    <Slider
                      value={[editDraft.quantity]}
                      max={100}
                      step={1}
                      onValueChange={([val]) =>
                        setEditDraft((p) => ({ ...p, quantity: val }))
                      }
                      className="flex-1"
                    />
                    <span className="w-12 text-sm font-medium text-right">
                      {editDraft.quantity}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 规格参数 */}
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="w-24 text-right shrink-0">
                    {t("toolbox.dialog.concentration", "规格浓度")}
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editDraft.concentration_ratio}
                    disabled={!!editDraft.global_product_id}
                    onChange={(e) =>
                      setEditDraft((p) => ({
                        ...p,
                        concentration_ratio: parseFloat(e.target.value),
                      }))
                    }
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground ml-[104px]">
                  {t(
                    "toolbox.dialog.concentrationHint",
                    "标准浓度为 1.0 (100%)",
                  )}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingId(null)}
              className="w-full sm:w-32"
            >
              {t("app.cancel", "取消")}
            </Button>
            <Button
              onClick={() =>
                editingId &&
                updateMutation.mutate({
                  id: editingId,
                  ...editDraft,
                })
              }
              disabled={updateMutation.isPending}
              className="w-full sm:w-32"
            >
              {updateMutation.isPending
                ? t("app.saving", "保存中...")
                : t("app.save", "保存")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 - 放在组件外部避免 Radix UI 事件冒泡问题 */}
      <AlertDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => {
          // 只有在没有正在删除的情况下才允许关闭
          if (!deletingRef.current) {
            if (!open) {
              // 关闭时清除 ref
              deleteItemIdRef.current = null;
            }
            setDeleteConfirm((prev) => ({ ...prev, open }));
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("toolbox.dialog.deleteTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("toolbox.dialog.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("app.cancel")}</AlertDialogCancel>
            {/* 使用普通 Button 替代 AlertDialogAction，避免 DialogClose 触发的 flushSync 导致多次事件 */}
            <Button
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // 再次检查防止重复点击
                if (deletingRef.current !== null) {
                  return;
                }
                handleDelete();
              }}
              disabled={deletingId !== null || deletingRef.current !== null}
            >
              {deletingId === deleteConfirm.itemId
                ? t("app.deleting")
                : t("app.delete")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
